import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskLine, EASE } from "./Reveal";
import { scrollToId } from "./SmoothScroll";
import { APPS } from "@/data/content";

const WINDOWS = [
  { src: "/assets/suite/pgr.webp", title: "FMaciel PGR", cls: "left-[2%] top-[0%] w-[74%]", z: -120, d: 0.5 },
  { src: "/assets/suite/obr.webp", title: "FMaciel Gestão de Obras", cls: "right-[0%] top-[18%] w-[74%]", z: -40, d: 0.65 },
  { src: "/assets/estruturas-app/4.webp", title: "FMaciel Estruturas · Modelo 3D", cls: "left-[8%] top-[40%] w-[80%]", z: 60, d: 0.8 },
];

const Window = ({ w }) => (
  <motion.div
    initial={{ opacity: 0, y: 90, rotateX: 25 }}
    animate={{ opacity: 1, y: 0, rotateX: 0 }}
    transition={{ duration: 1.4, ease: EASE, delay: w.d }}
    style={{ transform: `translateZ(${w.z}px)` }}
    className={`absolute ${w.cls} rounded-md overflow-hidden border border-white/15 bg-[#0B132B] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.85)]`}
  >
    <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-white/10">
      <span className="h-1.5 w-1.5 rounded-full bg-white/30" /><span className="h-1.5 w-1.5 rounded-full bg-white/30" /><span className="h-1.5 w-1.5 rounded-full bg-white/30" />
      <span className="ml-2 font-mono text-[10px] text-[var(--steel-2)] truncate">{w.title}</span>
    </div>
    <img src={w.src} alt={w.title} className="block w-full aspect-[16/8.4] object-cover object-top bg-white" />
  </motion.div>
);

const HeroStack = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [16, 0]), { stiffness: 90, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-24, -6]), { stiffness: 90, damping: 16 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-stack" className="relative w-full aspect-[10/8] [perspective:1600px] max-lg:scale-[0.88] max-lg:origin-left">
      <div className="absolute inset-[10%] rounded-full bg-[radial-gradient(circle,rgba(47,107,255,0.35)_0%,transparent_65%)] blur-2xl" />
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative w-full h-full">
        {WINDOWS.map((w) => <Window key={w.src} w={w} />)}
        <motion.img
          src="/assets/mark-t.png"
          alt=""
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: EASE, delay: 1.1 }}
          style={{ transform: "translateZ(140px)" }}
          className="absolute -left-[4%] -bottom-[6%] w-[24%] logo-glow"
        />
      </motion.div>
    </div>
  );
};

export const HeroSuite = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const stackY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden grain bg-[var(--ink)]">
      <div className="absolute inset-0 blueprint opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--ink)]" />
      <span className="crosshair left-6 top-24" /><span className="crosshair right-6 top-24" />

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pt-32 pb-16 lg:pt-36 grid lg:grid-cols-12 gap-12 items-center min-h-[100svh]">
        <motion.div style={{ y: textY }} className="lg:col-span-6">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--cyan)] mb-8 flex items-center gap-3">
            <span className="h-2 w-2 shrink-0 rounded-full bg-[var(--amber)] animate-pulse" />
            <span>Consultoria e Projetos Mecânicos · <span data-testid="hero-location" className="whitespace-nowrap">Manaus, AM</span></span>
          </motion.p>
          <h1 data-testid="hero-title" className="font-display font-black uppercase leading-[0.88] tracking-tight text-[3.3rem] sm:text-7xl lg:text-[6.6rem]">
            <MaskLine delay={0.35}><span className="text-white">Engenharia</span></MaskLine>
            <MaskLine delay={0.5}><span className="steel-text">que transforma</span></MaskLine>
            <MaskLine delay={0.65}><span className="text-white">projetos em</span></MaskLine>
            <MaskLine delay={0.8}><span className="text-[var(--blue)]">resultados.</span></MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg text-[var(--steel-2)] leading-relaxed">
            Projetos de combate a incêndio, ar comprimido, utilidades e estruturas metálicas,
            consultorias e treinamentos. E uma linha própria de aplicativos de engenharia usados em obra,
            manutenção e segurança do trabalho.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-cta-apps" onClick={() => scrollToId("aplicativos")} className="group inline-flex items-center gap-3 rounded-full bg-[var(--blue)] text-white px-7 py-4 font-medium hover:bg-[#4a7dff] transition-colors">
              Ver os aplicativos <ArrowUpRight size={18} className="transition-transform group-hover:rotate-45" />
            </button>
            <button data-testid="hero-cta-services" onClick={() => scrollToId("servicos")} className="group inline-flex items-center gap-3 rounded-full border border-white/25 px-7 py-4 font-medium text-white hover:border-[var(--cyan)] hover:text-[var(--cyan)] transition-colors">
              Serviços de engenharia <ArrowDown size={18} className="transition-transform group-hover:translate-y-1" />
            </button>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }} className="mt-12 flex flex-wrap items-center gap-x-4 gap-y-3">
            <div className="flex -space-x-3">
              {APPS.map((a) => (
                <span key={a.id} className="grid h-9 w-9 sm:h-11 sm:w-11 place-items-center rounded-full border border-white/15 bg-[var(--navy)]">
                  <img src={a.logo} alt={a.name} className="h-6 w-6 sm:h-8 sm:w-8 object-contain" />
                </span>
              ))}
            </div>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--steel-2)]">8 aplicativos · FMACIEL Suite</span>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: stackY }} className="lg:col-span-6 min-w-0">
          <HeroStack />
        </motion.div>
      </div>
    </section>
  );
};
